import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Loading } from '@src/common/Loading';
import { PageLayout } from '@src/common/PageLayout';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { config } from '@uhg-abyss/web/tools/config';
import { useTinRegistration } from '@src/state/tinRegistration';
import { event } from '@uhg-abyss/web/tools/event';
import isEmpty from 'lodash/isEmpty';
import { useUser } from '@src/state/user/hooks';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useTinDrafts } from '@src/state/superUserDraft';
import { BillingTin } from './Inputs/BillingTin';
import { Tin } from './Inputs/Tin';
import { OptumId } from './Inputs/OptumId';
import { DraftValidationModel } from './DraftValidation/DraftValidation';

const BoxHeader = styled('div', {
  position: 'relative',
  height: '120px',
  marginTop: '-62px',
  borderBottom: '1px solid #dadbdc',
});

const ClipBoardIcon = styled('div', {
  margin: 'auto',
  marginTop: '14px',
  display: 'block',
  width: '50px',
});

const Heading = styled('div', {
  fontWeight: 600,
  fontSize: '22px',
  textAlign: 'center',
  paddingTop: '70px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const InputField = styled('div', {
  margin: '-6px 120px 20px 120px',
});

const InputDescription = styled('p', {
  fontSize: '14px',
  textAlign: 'center',
  lineHeight: '18px',
  fontWeight: 500,
  padding: '20px',
  fontFamily: '$primary',
});

const StartButton = styled('div', {
  margin: 'auto',
  width: '75%',
  paddingTop: '25px',
  paddingBottom: '26px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  display: 'flex',
  justifyContent: 'center',
  '& button': {
    width: '85%',
    paddingLeft: 'inherit',
  },
});

const Layout = styled('div', {
  height: '100%',
  margin: 'auto',
  boxShadow: '0 0 3px 0 rgba(0, 0, 0, 0.5)',
  borderRadius: '3px',
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
});

const HeaderAndInput = styled('div', {
  height: '75%',
});

export const TinRegistration = ({
  errorRes,
  setErrorRes,
  setIsAlertBanner,
  setTinRegError,
  setTinRegStatusError,
  onYes,
  populateDetails,
  setIsUserBlocked,
  setTimeRemaining,
}) => {
  const router = useRouter();
  const form = useForm();
  const userInfo = useUser.state();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const {
    fetchTinStatus,
    setTrackingNumber,
    toggleReturn,
    disableSaveAsDraft,
  } = useTinRegistration.actions();
  const { tinDraftList, fetchTinStatusSaga } = useTinRegistration.state();
  const { isCheckoutUser } = userInfo;
  const registration = form.getValues('searchRegistration');
  const searchDraft = form.getValues('searchDraft');
  const tinDraft = Object.values(tinDraftList);

  const { retrieveSuperUserDraft } = useTinDrafts.actions();

  const [isDraftValidationModalOpen, setDraftValidationModalOpen] =
    useState(false);

  const closeDraftValidation = () => {
    setDraftValidationModalOpen(false);
  };

  const startNewRegistration = () => {
    toggleReturn(false);
    setIsAlertBanner(false);
    setIsUserBlocked(false);

    form.validate(
      'searchRegistration',
      (values) => {
        const { billingTin, requestId } = values;
        const updatedBillingTin = billingTin.replace(/-/g, '');
        setTinRegStatusError(false);
        fetchTinStatus(
          {
            billingTin: updatedBillingTin,
            requestId,
            test: isCheckoutUser,
          },
          (res) => {
            setTrackingNumber(res.trackingNumber);
            form.setValue('searchRegistration.billingTin', updatedBillingTin);
            event('TIN_REGISTRATION_START');
            router.navigate('/tin-registration/step/1');
          },
          (error) => {
            event('API_TRANSACTION_ERROR', {
              code: error.faultCode,
              message: error.message,
            });
            if (error.faultCode === 'EPTSUB' && error.timestamp) {
              const ms = Date.parse(error.timestamp) - Date.now();
              setTimeRemaining(ms);
              setIsUserBlocked(true);
            } else {
              setErrorRes({ error });
              setIsAlertBanner(true);
              setTinRegStatusError(false);
            }

            if (
              errorRes.error.statusCode === '500' ||
              errorRes.error.statusCode === '401'
            ) {
              setTinRegError(true);
            }
          }
        );
      },
      (error) => {
        const fields = {};
        if (isEmpty(error.billingTin)) {
          fields.billingTin = '';
        }
        if (!isEmpty(fields)) {
          event('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const handleStartRegistration = () => {
    if (isSuperUser) {
      const { tin, optumId } = searchDraft;
      form.validate('searchRegistration', (values) => {
        retrieveSuperUserDraft(
          {
            billingTin: tin,
            optumId,
          },
          (res) => {
            setTrackingNumber(res.trackingNumber);
            form.setValue('searchRegistration.billingTin', tin);
            form.setValue('searchRegistration.optumId', optumId);
            form.setValue('superUserDraft', res);
            populateDetails(res);
          },
          (error) => {
            setTinRegStatusError(true);
          }
        );
      });
    } else if (
      tinDraft &&
      tinDraft.length &&
      tinDraft.some((element) => {
        return element.billingTin === registration.billingTin;
      })
    ) {
      tinDraft.forEach((element) => {
        if (element && element.billingTin === registration.billingTin) {
          disableSaveAsDraft(false);
          onYes(element);
        }
      });
    } else {
      startNewRegistration();
    }
  };

  const checkDraft = () => {
    if (tinDraft != null && tinDraft.length === 5) {
      disableSaveAsDraft(true);
      setDraftValidationModalOpen(true);
    } else {
      disableSaveAsDraft(false);
      handleStartRegistration();
    }
  };

  return (
    <PageLayout>
      <Loading isLoading={fetchTinStatusSaga.isLoading}>
        <DraftValidationModel
          isOpen={isDraftValidationModalOpen}
          closeModal={closeDraftValidation}
          handleStartRegistration={handleStartRegistration}
        />

        {isSuperUser ? (
          <Layout>
            <HeaderAndInput>
              <ClipBoardIcon>
                <IconBrand icon="clipboard" size="50px" variant="twotone" />
              </ClipBoardIcon>
              <BoxHeader>
                <Heading id="dashboard-searchDraft">
                  Search for Taxpayer Identification Number (TIN)
                </Heading>
              </BoxHeader>
              <InputDescription id="dashboard-searchDraftText">
                Begin the registration process by entering the TIN used for
                claims billing. Registering your TIN will not initiate a
                contract with UnitedHealthcare.
              </InputDescription>
              <Grid>
                <Grid.Col span="5%" />
                <Grid.Col span="45%">
                  <Tin />
                </Grid.Col>
                <Grid.Col span="45%">
                  <OptumId />
                </Grid.Col>
              </Grid>
            </HeaderAndInput>
            <div>
              <StartButton>
                <Button
                  type="button"
                  id="search-draft-button"
                  aria-label="click here to Search Billing TIN Draft"
                  data-testid="search-button"
                  onClick={handleStartRegistration}
                  width="100%"
                >
                  Search
                </Button>
              </StartButton>
            </div>
          </Layout>
        ) : (
          <Layout>
            <HeaderAndInput>
              <ClipBoardIcon>
                <IconBrand icon="clipboard" size="50px" variant="twotone" />
              </ClipBoardIcon>
              <BoxHeader>
                <Heading id="dashboard-tinRegistration">
                  Register your Taxpayer Identification Number (TIN)
                </Heading>
              </BoxHeader>
              <InputDescription id="dashboard-tinRegistrationText">
                Begin the registration process by entering the TIN used for
                claims billing. Registering your TIN will not initiate a
                contract with UnitedHealthcare.
              </InputDescription>
              <InputField>
                <BillingTin />
              </InputField>
            </HeaderAndInput>
            <StartButton>
              <Button
                type="button"
                id="startregistration-button"
                aria-label="click here to Register Billing TIN"
                data-testid="start-button"
                onClick={checkDraft}
                width="100%"
                isDisabled={config('US3671382_SUPER_USER') && isSuperUser}
              >
                Start Now
              </Button>
            </StartButton>
          </Layout>
        )}
      </Loading>
    </PageLayout>
  );
};

TinRegistration.propTypes = {
  setErrorRes: PropTypes.func.isRequired,
  errorRes: PropTypes.shape({
    error: PropTypes.shape({
      statusCode: PropTypes.number,
      faultCode: PropTypes.string,
      message: PropTypes.string,
    }),
  }).isRequired,
  setIsAlertBanner: PropTypes.func.isRequired,
  setTinRegError: PropTypes.func.isRequired,
  setTinRegStatusError: PropTypes.func.isRequired,
  onYes: PropTypes.func.isRequired,
  populateDetails: PropTypes.func.isRequired,
  setIsUserBlocked: PropTypes.func.isRequired,
  setTimeRemaining: PropTypes.func.isRequired,
};

TinRegistration.defaultProps = {};
